import React, { useState } from 'react';
import { LookupResult, codeFromScan } from './scannerUtils';

interface ScannerApi {
  lookup?: (q: string) => Promise<{
    results: LookupResult[];
  }>;
}

interface ScannerManualInputProps {
  api: ScannerApi;
  onScan: (code: string) => void;
  hasLookup: boolean;
}

export function ScannerManualInput({
  api,
  onScan,
  hasLookup,
}: ScannerManualInputProps): JSX.Element {
  const [manual, setManual] = useState('');
  const [lookup, setLookup] = useState<LookupResult[]>([]);

  const search = async (q: string): Promise<void> => {
    setManual(q);
    const code = codeFromScan(q);

    // If it looks like a code (16+ chars, alphanumeric), don't search
    if (
      q.trim().length < 2 ||
      /^[A-Z0-9-]{16,}$/i.test(q.trim()) ||
      code.length >= 16
    ) {
      setLookup([]);
      return;
    }

    if (!api.lookup) {
      setLookup([]);
      return;
    }

    try {
      setLookup((await api.lookup(q)).results);
    } catch {
      setLookup([]);
    }
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!manual) return;
    onScan(manual);
    setManual('');
    setLookup([]);
  };

  const handleSelectResult = (rawCode: string): void => {
    onScan(rawCode);
    setManual('');
    setLookup([]);
  };

  // Styles
  const inputStyle: React.CSSProperties = {
    flex: 1,
    borderRadius: 8,
    background: 'rgba(255,255,255,.1)',
    border: '1px solid rgba(255,255,255,.2)',
    padding: '10px 12px',
    color: '#fff',
    fontSize: 16,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0 16px',
    borderRadius: 8,
    background: 'rgba(255,255,255,.15)',
    color: '#fff',
    border: 0,
    fontWeight: 600,
    cursor: 'pointer',
  };

  return (
    <>
      <form
        style={{ width: '100%' }}
        onSubmit={handleSubmit}
        aria-label="Saisie manuelle du code"
      >
        <label
          style={{ fontSize: 14, color: 'rgba(255,255,255,.7)' }}
          htmlFor="manual-code-input"
        >
          {hasLookup
            ? 'Code du billet ou nom de la personne'
            : 'Code du billet'}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <input
              id="manual-code-input"
              value={manual}
              onChange={async (e) => await search(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              placeholder={hasLookup ? 'ABCD-EFGH-… ou Martin' : 'ABCD-EFGH-…'}
              style={inputStyle}
              aria-label={
                hasLookup
                  ? 'Code du billet ou nom de la personne'
                  : 'Code du billet'
              }
              aria-describedby="code-help"
            />
            <button
              type="submit"
              style={buttonStyle}
              aria-label="Valider le code"
            >
              Valider
            </button>
          </div>
          <span id="code-help" style={{ display: 'none' }}>
            {hasLookup
              ? 'Entrez le code du billet ou le nom de la personne'
              : 'Entrez le code du billet'}
          </span>
        </label>
      </form>

      {/* Lookup results */}
      {lookup.length > 0 && (
        <div
          style={{
            width: '100%',
            borderRadius: 8,
            background: 'rgba(255,255,255,.05)',
          }}
        >
          {lookup.map((t) => (
            <button
              key={t.raw_code}
              type="button"
              disabled={t.status !== 'valid'}
              onClick={() => handleSelectResult(t.raw_code)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                background: 'none',
                border: 0,
                borderBottom: '1px solid rgba(255,255,255,.1)',
                color: '#fff',
                cursor: t.status === 'valid' ? 'pointer' : 'not-allowed',
                opacity: t.status === 'valid' ? 1 : 0.6,
              }}
            >
              <b>{t.name}</b>
              {t.company && (
                <span style={{ color: 'rgba(255,255,255,.6)' }}>
                  {' '}
                  · {t.company}
                </span>
              )}
              <span
                style={{
                  display: 'block',
                  fontSize: 12,
                  color: 'rgba(255,255,255,.5)',
                }}
              >
                {t.type} · {t.code}
                {t.status !== 'valid'
                  ? ` · ${t.status === 'cancelled' ? 'annulé' : 'non payé'}`
                  : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

export default ScannerManualInput;
